import React, { useState, useEffect } from 'react';
import { faBuilding, faUsers, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../hooks/useAuth';
import FormInput from '../components/FormInput';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { OrganizationService } from '../services/organizationService';
import { AuthService } from '../services/authService';
import { organizationSetupSchema, OrganizationSetupFormData } from '../schemas';
import { COMPANY_SIZES, PendingOrganization } from '../types';

const OrganizationSetupPage: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pendingOrg, setPendingOrg] = useState<PendingOrganization | undefined>(
    user?.user_metadata?.pending_organization
  );

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { isValid }
  } = useForm<OrganizationSetupFormData>({
    resolver: zodResolver(organizationSetupSchema),
    mode: 'onChange',
    defaultValues: {
      organizationName: '',
      organizationSlogan: ''
    }
  });

  const onSubmit = async (data: OrganizationSetupFormData): Promise<void> => {
    if (!user) {
      setError('Usuário não encontrado');
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      const orgResult = await OrganizationService.createUserOrganization({
        userId: user.id,
        organizationName: data.organizationName,
        organizationSlogan: data.organizationSlogan,
        companySize: data.companySize
      });

      if (!orgResult.success) {
        throw new Error(orgResult.message);
      }

      // Clear pending organization data from user metadata
      if (pendingOrg) {
        await AuthService.removeMetadataFields(['pending_organization']);
      }

      // Refresh the page to reload the organizations
      window.location.reload();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Erro inesperado durante a criação da organização';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      const pendingOrg = user.user_metadata?.pending_organization;
      setPendingOrg(pendingOrg);
      reset(pendingOrg);
    }
  }, [reset, user]);

  // Redirect if user already has an organization
  useEffect(() => {
    if (currentOrganization) {
      navigate('/');
    }
  }, [currentOrganization, navigate]);

  // If user already has an organization, don't render the form
  if (currentOrganization) {
    return null;
  }

  const companySizeOptions = COMPANY_SIZES.map(size => ({
    value: size,
    label: `${size} funcionários`
  }));

  return (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100">
              <img
                className="w-10 h-10 object-contain"
                src="/logoipsum-custom-logogram.svg"
                alt="OptoSoft Logo"
              />
            </div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">OptoSoft</h1>
        <p className="text-gray-600 text-sm">Sistema de Gestão Clínica</p>
      </div>

      {/* Organization Setup Form */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">
            {pendingOrg ? 'Finalizar Cadastro' : 'Configurar Organização'}
          </h2>
          <p className="text-gray-600 text-sm">
            {pendingOrg
              ? 'Complete o cadastro da sua organização para acessar o sistema'
              : 'Para continuar, você precisa configurar sua organização'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Organization Name Field */}
          <FormInput
            name="organizationName"
            control={control}
            id="organizationName"
            type="text"
            required
            label="Nome da Organização"
            leftIcon={faBuilding}
            placeholder="Nome da sua clínica ou consultório"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Organization Slogan Field */}
            <FormInput
              name="organizationSlogan"
              control={control}
              id="organizationSlogan"
              type="text"
              label="Slogan (Opcional)"
              placeholder="Slogan da sua organização"
            />

            {/* Company Size Field */}
            <div>
              <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 mb-1">
                Tamanho da Empresa
              </label>
              <div className="relative">
                <select
                  id="companySize"
                  {...register('companySize')}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Selecione o tamanho</option>
                  {companySizeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faUsers} className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && <Alert variant="error" title="Erro na Configuração" message={error} />}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading || !isValid}
            leftIcon={!isLoading ? faUserPlus : undefined}
          >
            {isLoading ? 'Criando Organização...' : 'Criar Organização'}
          </Button>
        </form>

        {/* Info Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Por que preciso criar uma organização?</strong>
              <br />
              Cada usuário deve pertencer a uma organização para acessar o sistema. Isso garante que
              seus dados sejam organizados e protegidos adequadamente.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6">
          <p className="text-xs text-gray-500 text-center">
            Desenvolvido por{' '}
            <a
              href="https://um0um.com.br"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              101 Soluções
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default OrganizationSetupPage;
